/**
 * @file 「選んで聞く」の対象 (何について聞くか) を組み立てる純関数である。
 *
 * @what: 参照の書式 `<種類>:<本体>` で対象を作り、重なりを外して並べ、押したボタンに合わせた
 *   呼び出しの文字列にする。本人の解答を含みうるもの (メモ由来か、書き込みのある紙面) が1つでも
 *   あるか (「AI 採点」を出すか) も返す。
 * @why: 対象 (何について) と問い (何を知りたい) を分ける、というのが「選んで聞く」の設計である
 *   (代表の判断、2026-09-08)。対象は配列で溜まり、押したボタンが役割 (解説 / 採点) を伝える。
 * @failure: 例外を投げない。対象が0件のときは呼び出しの文字列を作らない (null を返す)。
 */

import type { StudyNote } from "../api/notes";
import type { StudySource } from "../api/sources";
import { printedPageOf } from "./sourcePages";

/** 選んだもの1つである。 */
export interface AskTarget {
  /** 参照の書式である (`page:shoaku-soron/18` など)。同じものを1つにまとめるときのキーにもなる。 */
  ref: string;
  /** 欄に出す短い名前である。 */
  label: string;
  /** メモ由来か (本人の解答を含みうるので「AI 採点」を出す根拠になる)。 */
  fromNote: boolean;
  /**
   * 本人の書き込みのある紙面か。メモ由来と同じく「AI 採点」を出す根拠になる。
   * ※なぜならサーバは紙面に本人の書き込みを重ねて渡し、AI はその手書きを本人の解答として読むためである。
   */
  inked: boolean;
}

/**
 * 選べる上限である。
 *
 * サーバが受ける上限と同じ数にする (usecase の studyAskMaxTargets)。※なぜなら web の側で多く選べると、
 * 押してから断られることになるためである。
 */
export const ASK_TARGETS_MAX = 4;

/** 資料のページを対象にする。inked はそのページに本人の書き込みがあるかである。 */
export function pageTarget(source: StudySource, readerPage: number, inked = false): AskTarget {
  return {
    ref: `page:${source.id}/${readerPage}`,
    label: `${source.title} p.${printedPageOf(source, readerPage)}`,
    fromNote: false,
    inked,
  };
}

/**
 * 紙面の一部 (囲んだ範囲) を対象にする。
 *
 * 割合は小数第3位で丸める。※なぜなら参照は記録に残る文字列で、桁が長いと読めなくなるためである。
 */
export function pageRectTarget(
  source: StudySource,
  readerPage: number,
  rect: { x: number; y: number; w: number; h: number },
  inked = false,
): AskTarget {
  const r = (v: number) => Math.round(v * 1000) / 1000;
  const x = r(rect.x);
  const y = r(rect.y);
  // 丸めで紙面の外へはみ出さないようにする。※なぜならサーバは、右下が紙面の外に出る指定を弾くためである。
  const w = Math.min(r(rect.w), 1 - x);
  const h = Math.min(r(rect.h), 1 - y);
  return {
    ref: `page:${source.id}/${readerPage}@${x},${y},${w},${h}`,
    label: `${source.title} p.${printedPageOf(source, readerPage)}（囲んだところ）`,
    fromNote: false,
    inked,
  };
}

/** 学びメモ全体を対象にする。 */
export function noteTarget(note: StudyNote): AskTarget {
  return { ref: `note:${note.id}`, label: noteLabel(note), fromNote: true, inked: false };
}

/** 学びメモの節を対象にする (見出しの文字をそのまま渡す)。 */
export function noteSectionTarget(note: StudyNote, heading: string): AskTarget {
  return {
    ref: `note:${note.id}#${heading}`,
    label: `${noteLabel(note)}／${heading}`,
    fromNote: true,
    inked: false,
  };
}

/** 学びメモの画像を対象にする。 */
export function noteImageTarget(note: StudyNote, imageId: string, order: number): AskTarget {
  return {
    ref: `image:${note.id}/${imageId}`,
    label: `${noteLabel(note)}／${order}枚目の画像`,
    fromNote: true,
    inked: false,
  };
}

function noteLabel(note: StudyNote): string {
  const title = note.title.trim();
  return title === "" ? "学びメモ" : title;
}

/**
 * 同じ参照は1つにまとめ、上限に達していたら足さない。
 *
 * ※なぜなら古いものを黙って落とすと、選んだはずのものが消えたことに気づけないためである。
 * 同じ参照でも、書き込みのある紙面として足し直されたときは、その印だけを付け替える。※なぜなら
 * 選んだあとに書いた紙面でも「AI 採点」を出せるようにするためである。
 * 変わらないときは同じ配列を返す (再描画を起こさない)。
 */
export function addTarget(targets: readonly AskTarget[], next: AskTarget): readonly AskTarget[] {
  const at = targets.findIndex((t) => t.ref === next.ref);
  if (at >= 0) {
    const found = targets[at]!;
    if (!next.inked || found.inked) return targets;
    return targets.map((t, i) => (i === at ? { ...t, inked: true } : t));
  }
  if (targets.length >= ASK_TARGETS_MAX) return targets;
  return [...targets, next];
}

export function removeTarget(targets: readonly AskTarget[], ref: string): readonly AskTarget[] {
  return targets.filter((t) => t.ref !== ref);
}

/** 本人の解答を含みうるもの (メモ由来か、書き込みのある紙面) が1つでもあるか (「AI 採点」を出すかの判定)。 */
export function canGrade(targets: readonly AskTarget[]): boolean {
  return targets.some((t) => t.fromNote || t.inked);
}

/**
 * Claude Code に貼る呼び出しの文字列を作る。
 *
 * 参照を空白で並べ、そのあとに問いを置く。※なぜならスキルは参照で読む量を決め、問いは
 * そのあとの文として受け取るためである。空白を含む参照 (見出しに空白があるメモの節) は
 * 二重引用符で囲む。囲まないと参照が2語に割れ、後ろが問いに混ざる。対象が無ければ null を返す。
 */
export function askCommand(
  preset: "ai_explain" | "ai_grade",
  targets: readonly AskTarget[],
  question: string,
): string | null {
  if (targets.length === 0) return null;
  const name = preset === "ai_grade" ? "/ai-grade" : "/ai-explain";
  const refs = targets.map((t) => (/\s/.test(t.ref) ? `"${t.ref}"` : t.ref)).join(" ");
  const q = question.trim();
  return q === "" ? `${name} ${refs}` : `${name} ${refs} ${q}`;
}
